import axios from "axios";

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Request interceptor — attach token from localStorage as Authorization header fallback
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token") || localStorage.getItem("session_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

// ── Connection tracking (school Wi-Fi drops) ──
export const connection = { online: true, listeners: new Set() };
const setOnline = (v) => {
  if (connection.online === v) return;
  connection.online = v;
  connection.listeners.forEach((l) => l(v));
  if (!v) probe();
};
let probing = false;
const probe = async () => {
  if (probing) return;
  probing = true;
  while (!connection.online) {
    await new Promise((r) => setTimeout(r, 3000));
    try { await API.get("/health", { timeout: 5000 }); } catch { /* still offline */ }
  }
  probing = false;
};
window.addEventListener("offline", () => setOnline(false));
window.addEventListener("online", () => API.get("/health").catch(() => {}));

export const waitForOnline = () =>
  connection.online ? Promise.resolve() : new Promise((resolve) => {
    const l = (v) => { if (v) { connection.listeners.delete(l); resolve(); } };
    connection.listeners.add(l);
  });

const isNetworkError = (err) => !err.response && err.code !== "ERR_CANCELED";

// Retries a request after the connection comes back
export async function withRetry(fn, tries = 30) {
  for (let i = 0; ; i++) {
    try {
      return await fn();
    } catch (err) {
      if (!isNetworkError(err) || i >= tries) throw err;
      await waitForOnline();
      await new Promise((r) => setTimeout(r, 300));
    }
  }
}

API.interceptors.response.use(
  (res) => { setOnline(true); return res; },
  async (error) => {
    if (isNetworkError(error)) setOnline(false);
    else if (error.response) setOnline(true);
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && !original.url?.includes("/auth/")) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => API(original));
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        const { data } = await API.post("/auth/refresh", refreshToken ? { refresh_token: refreshToken } : {});
        if (data.access_token) {
          localStorage.setItem("access_token", data.access_token);
        }
        processQueue(null);
        return API(original);
      } catch (err) {
        processQueue(err);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("session_token");
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default API;
